import { StandardButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { CurbyHeader } from '@/components/curby-header';
import { UserBagDropOffInfo } from '@curby/shared';
import { Alert, Card, Flex, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { MaterialType } from '../../../../../backend/src/types.generated';
import { useBagScanContext } from '../hook';
import styles from '../styles.module.css';

export const BagScanResultPage = () => {
	const { scannedBag, scanAnotherBag } = useBagScanContext();
	const navigate = useNavigate();

	const scannedMessage = useMemo(() => {
		const prefix = 'Thank you for recycling your';
		const suffix = 'with Curby!';
		switch (scannedBag?.material) {
			case MaterialType.BlendedFibre:
				return `${prefix} blended fibre ${suffix}`;
			case MaterialType.Cardboard:
				return `${prefix} cardboard ${suffix}`;
			case MaterialType.CoffeePods:
				return `${prefix} coffee pods ${suffix}`;
			case MaterialType.LiquidPaperBoard:
				return `${prefix} liquid paperboard ${suffix}`;
			case MaterialType.MixedPaper:
				return `${prefix} mixed paper ${suffix}`;
			case MaterialType.MixedPlastic:
				return `${prefix} mixed plastic ${suffix}`;
			case MaterialType.SoftPlastic:
				return `${prefix} soft plastic ${suffix}`;
			case MaterialType.Textiles:
				return `${prefix} textiles ${suffix}`;
			/**
			 * The user-facing messaging here shouldn't
			 * be any more specific.
			 */
			case MaterialType.GlassCoarse:
			case MaterialType.GlassMedium:
			case MaterialType.GlassFine:
			case MaterialType.GlassSuperfine:
			case MaterialType.GlassMoondust:
			case MaterialType.GlassSf_300_150:
			case MaterialType.GlassSf_500_300:
				return `${prefix} glass ${suffix}`;
			default:
				return `${prefix} ${scannedBag?.material ?? 'material'} ${suffix}`;
		}
	}, [scannedBag?.material]);

	const dropOffInfo = useMemo(() => {
		if (scannedBag?.createdByUser?.nearestDropOff) {
			return scannedBag?.createdByUser?.nearestDropOff as UserBagDropOffInfo;
		}
		return null;
	}, [scannedBag?.createdByUser?.nearestDropOff]);

	return (
		<Flex pos="absolute" h="100vh" pt={100} w="100%" bg="var(--mantine-color-green-0)">
			<motion.div
				initial={{ opacity: 0, y: -50 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, ease: 'easeOut' }}
				className={styles.scanResultHeader}
			>
				<IconArrowLeft onClick={() => navigate({ to: '/home' })} />
				<Title order={2}>Scan a Bag</Title>
			</motion.div>
			<ContentPanel gap="md">
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
				>
					<CurbyHeader flexProps={{ direction: 'row', align: 'flex-start', gap: 'sm', w: '100%' }}>
						<Flex direction="column" className={styles.entries}>
							<Title order={1} ta="center">
								Scan Success!
							</Title>
							<Text size="md" ta="center">
								{scannedMessage}
							</Text>
							{Boolean(scannedBag?.entriesGained && scannedBag.entriesGained > 0) && (
								<Text size="md" ta="center">
									You have gained {scannedBag?.entriesGained} entries!
								</Text>
							)}
						</Flex>
					</CurbyHeader>
				</motion.div>

				{dropOffInfo?.bagPickup ? (
					<>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.4, ease: 'easeOut' }}
						>
							<Alert
								variant="filled"
								bg="var(--mantine-color-orange-2)"
								mt="sm"
								title="Don't forget to place your bag into your yellow lid recycling bin!"
								className={styles.scanResultAlert}
								icon={<IconInfoCircle className={styles.scanResultAlertIcon} size={30} />}
							/>
						</motion.div>
						{dropOffInfo?.nearestDropOff && (
							<motion.div
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.6, ease: 'easeOut' }}
								className={styles.scanResultCard}
							>
								<Card radius="lg" w="100%" withBorder>
									<Title order={3}>Prefer drop off?</Title>
									<Text size="sm">You can drop off your Curby bag at the below location:</Text>
									<Flex>
										<Flex pt="lg" direction="column">
											<Text size="md">
												{dropOffInfo.nearestDropOff.name}
												{dropOffInfo.nearestDropOff.distance &&
													` (${dropOffInfo.nearestDropOff.distance}km)`}
											</Text>
											{dropOffInfo.nearestDropOff.address && (
												<Text fw="normal" size="xs">
													{dropOffInfo.nearestDropOff.address}
												</Text>
											)}
										</Flex>
									</Flex>
								</Card>
							</motion.div>
						)}
					</>
				) : (
					dropOffInfo?.nearestDropOff && (
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.4, ease: 'easeOut' }}
							className={styles.scanResultCard}
						>
							<Card radius="lg" w="100%" withBorder>
								<Title order={3}>Bag Drop Location</Title>
								<Flex>
									<Flex direction="column">
										<Text size="sm">
											{dropOffInfo.nearestDropOff.name}
											{dropOffInfo.nearestDropOff.distance &&
												` (${dropOffInfo.nearestDropOff.distance}km)`}
										</Text>
										{dropOffInfo.nearestDropOff.address && (
											<Text fw="normal" size="xs">
												{dropOffInfo.nearestDropOff.address}
											</Text>
										)}
									</Flex>
								</Flex>
							</Card>
						</motion.div>
					)
				)}
			</ContentPanel>
			<motion.div
				initial={{ opacity: 0, y: 50 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.8, ease: 'easeOut' }}
				className={styles.scanResultFooter}
			>
				<Flex w="100%" gap="sm">
					<StandardButton variant="default" onClick={scanAnotherBag} fullWidth>
						Scan Another
					</StandardButton>
					<StandardButton onClick={() => navigate({ to: '/home' })} fullWidth>
						Done
					</StandardButton>
				</Flex>
			</motion.div>
		</Flex>
	);
};
